import classNames from 'classnames';
import { useAssertedActiveTabId } from 'hooks/use-asserted-active-tab-id';
import { useAppSelector } from 'hooks/useAppSelector';
import { ComponentProps } from 'react';
import { BlockItem } from 'routes/essential-workspace/components/form/blocks';
import { FieldItem } from 'routes/essential-workspace/components/form/fields';
import { getEssentialNodeClassName } from 'routes/essential-workspace/components/form/helpers';
import { selectEssentialFormNodes } from 'routes/essential-workspace/state/form.selectors';

import './style.scss';
import { EssentialFormNode, isEssentialBlockNode } from 'routes/essential-workspace/state/model';

type EssentialFormProps = Omit<ComponentProps<'div'>, 'children'> & {
  currentSectionId: string;
  isLayoutEditing: boolean;
};

export function EssentialForm({ className, currentSectionId, isLayoutEditing, ...props }: EssentialFormProps) {
  const tabId = useAssertedActiveTabId();
  const formNodes = useAppSelector(state => selectEssentialFormNodes(state, tabId, currentSectionId, isLayoutEditing));

  return (
    <div
      className={classNames('grid gap-4 essential-form', isLayoutEditing && 'essential-form-layout-editing', className)}
      {...props}
      data-e2e="essential-form"
    >
      {formNodes.map(node => (
        <FormTreeNode key={node.id} node={node} currentSectionId={currentSectionId} isLayoutEditing={isLayoutEditing} />
      ))}
    </div>
  );
}

interface FormTreeNodeProps {
  node: EssentialFormNode;
  currentSectionId: string;
  isLayoutEditing: boolean;
}

function FormTreeNode({ node, currentSectionId, isLayoutEditing }: FormTreeNodeProps) {
  const className = getEssentialNodeClassName(node.width);

  if (isEssentialBlockNode(node)) {
    const isActiveSection = node.id === currentSectionId;

    return (
      <div
        data-node-id={node.id}
        className={classNames(
          className,
          isLayoutEditing && 'essential-layout-editing',
          isLayoutEditing && isActiveSection && 'essential-layout-active-section',
          isLayoutEditing && !isActiveSection && 'essential-layout-inactive-section',
        )}
      >
        <BlockItem {...node}>
          {node.nodes.map(blockNode => (
            <FormTreeNode
              key={blockNode.id}
              node={blockNode}
              currentSectionId={currentSectionId}
              isLayoutEditing={isLayoutEditing}
            />
          ))}
        </BlockItem>
      </div>
    );
  }

  return node.isHidden ? null : (
    <div data-node-id={node.id} className={className}>
      <FieldItem {...node} />
    </div>
  );
}
